import { useMemo, useState } from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import type { GridColDef } from '@mui/x-data-grid';
import { DataTable, LoadingState } from '@radiology/ui';
import dayjs from 'dayjs';
import { STATUS_TONE, money, useRequests, type PurchaseRequest, type RequestStatus } from './api';

/**
 * The one purchase request table, shared by every role's screens.
 *
 * What each role sees is decided by RLS, not by props — an employee and an
 * approver render this same component and get different rows. `showRequester`
 * only controls whether the requester's name is a useful column, which it is not
 * when every row is your own.
 *
 * `allowExport` on DataTable replaces the legacy ExportExcelBtn: the old app
 * pulled every request into the browser and built an xlsx with `xlsx` +
 * `file-saver`. Exporting the rows RLS actually returned is both simpler and
 * narrower.
 */
export function RequestTable({
  status,
  showRequester = true,
  emptyTitle,
  emptyDescription,
  actionLabel,
  onAction,
}: {
  status?: RequestStatus | RequestStatus[];
  showRequester?: boolean;
  emptyTitle: string;
  emptyDescription?: string;
  actionLabel?: string;
  onAction?: (request: PurchaseRequest) => void;
}) {
  const { data, isLoading, error, refetch } = useRequests(status);
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    return data.filter(r =>
      [
        r.description,
        r.serial_no,
        r.cat_no ?? '',
        r.spr_number ?? '',
        r.creator_username,
        r.creator_section,
        r.department ?? '',
      ]
        .filter(Boolean)
        .some(v => v.toLowerCase().includes(needle)),
    );
  }, [data, search]);

  const columns = useMemo<GridColDef<PurchaseRequest>[]>(() => {
    const cols: GridColDef<PurchaseRequest>[] = [
      {
        field: 'description',
        headerName: 'Item',
        flex: 1.5,
        minWidth: 220,
        renderCell: params => (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2">{params.row.description}</Typography>
            <Typography variant="caption" color="text.secondary">
              S/N {params.row.serial_no}
              {params.row.cat_no ? ` · Cat ${params.row.cat_no}` : ''}
              {params.row.existing_item === 'Yes' ? ' · existing item' : ''}
            </Typography>
          </Stack>
        ),
      },
    ];

    if (showRequester) {
      cols.push({
        field: 'creator_username',
        headerName: 'Requested by',
        flex: 1,
        minWidth: 170,
        renderCell: params => (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2">{params.row.creator_username}</Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.creator_hospital} · {params.row.creator_section}
            </Typography>
          </Stack>
        ),
      });
    }

    cols.push(
      {
        field: 'quantity',
        headerName: 'Qty',
        width: 80,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'total',
        headerName: 'Total',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => (
          <Stack sx={{ py: 0.5, alignItems: 'flex-end' }}>
            <Typography variant="body2">{money(params.row.total)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {money(params.row.unit_price)} each
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        renderCell: params => {
          const value = params.value as RequestStatus;
          const tone = STATUS_TONE[value];
          return (
            <Chip
              size="small"
              label={value}
              color={tone === 'default' ? undefined : tone}
              variant={tone === 'default' ? 'outlined' : 'filled'}
            />
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Raised',
        width: 150,
        valueGetter: value => (value ? dayjs(value).format('D MMM YYYY') : ''),
        renderCell: params => (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2">{params.value as string}</Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.spr_number
                ? `SPR ${params.row.spr_number}`
                : (params.row.creator.title ?? '')}
            </Typography>
          </Stack>
        ),
      },
    );

    if (onAction) {
      cols.push({
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => (
          <Button size="small" variant="outlined" onClick={() => onAction(params.row)}>
            {actionLabel ?? 'Review'}
          </Button>
        ),
      });
    }

    return cols;
  }, [showRequester, onAction, actionLabel]);

  if (isLoading) return <LoadingState variant="skeleton" rows={6} />;

  return (
    <DataTable<PurchaseRequest>
      rows={rows}
      columns={columns}
      getRowId={row => row.request_id}
      getRowHeight={() => 56}
      search={{ value: search, onChange: setSearch, placeholder: 'Search item, S/N, requester…' }}
      empty={{ title: emptyTitle, description: emptyDescription }}
      error={error ? { message: error.message, onRetry: () => void refetch() } : undefined}
      allowExport
    />
  );
}
